/**
 * The anonymous usage counter (CLAUDE.md hard constraints: the one network exception; Worker in workers/counter). When
 * a check completes the app sends one POST whose whole body is two whole numbers, "documents,shekels" (Matt's counter
 * expansion, 2026-10-22 13:05; docs/backlog.md): how many documents this check read that weren't counted yet (payslips
 * and Form 106s read from a file, and a Form 106 typed in; a failed read or a typed-in payslip adds nothing), and how much the money worth checking
 * grew, in whole shekels: each worth-checking figure rounded down to ₪100, added up (the Form 106 refund estimate's low
 * end only where we estimate one). Nothing new → no request. The start screen reads the totals. No cookies (credentials
 * omitted), no Referer, no ID, nothing printed on any document. "Don't count my checks" (privacy page, kept on this
 * device) stops every counter request. A build without MAGIA_COUNTER_URL has no counter: nothing is sent or read, and
 * the CSP's connect-src stays 'none' (vite.config.ts).
 */
import { claimableTotal, type Finding } from '../model';

export const COUNTER_URL: string = __COUNTER_URL__;

/** The message key for this build: a text that differs by build has the base key (no counter) and `<key>Counter` (counter build). */
export const counterKey = (key: string, url: string = COUNTER_URL): string => (url ? `${key}Counter` : key);

const OPTIONS = { credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-store', mode: 'cors' } as const;
/**
 * What this page already counted (memory only, gone on reload; never sent, so it links nothing): the documents already
 * counted, by their place in this visit (file name and page; the name stays on the device), and the money already sent.
 * A later check in the same visit (a Form 106 added, a value changed) sends only what is new, so nothing is counted twice.
 */
const countedDocs = new Set<string>();
let sentShekels = 0;

/** "Don't count my checks" (privacy page): kept in this browser only. */
export const NO_COUNT_KEY = 'magia-li.noCount';
export function countingOff(): boolean {
  try {
    return localStorage.getItem(NO_COUNT_KEY) === '1';
  } catch {
    return false;
  }
}
export function setCountingOff(off: boolean): void {
  try {
    if (off) localStorage.setItem(NO_COUNT_KEY, '1');
    else localStorage.removeItem(NO_COUNT_KEY);
  } catch {
    // Storage blocked: the switch can't be kept; nothing else to do.
  }
}

/** Tests only: a new page load. */
export const resetSent = (): void => {
  sentShekels = 0;
  countedDocs.clear();
};

/** One figure (agorot) as counted: whole shekels, rounded DOWN to ₪100 (the low end), never negative. */
export const roundedLow = (agorot: number): number => Math.max(0, Math.floor(agorot / 10_000) * 100);

/** The most documents one request may add (the Worker refuses more); a larger check counts this many, so it only understates. */
export const MAX_DOCUMENTS = 50;

/**
 * The whole POST body: "documents,shekels", digits and one comma, nothing else (the Worker refuses any other shape).
 * Exported for the payload test.
 */
export const payload = (documents: number, shekels: number): string => `${documents},${shekels}`;

/** One check: the documents it read (keys that stay on the device) and its money worth checking, already in whole shekels. */
export interface CheckCount {
  /** One key per document read (a payslip page or a Form 106 file); never sent. */
  documents: readonly string[];
  /** The sum of the check's worth-checking figures, each rounded down to ₪100 (roundedLow), in whole shekels. */
  shekels: number;
}

/** What a completed check holds, as the app has it (src/ui/app.ts goTo). */
export interface CheckInput {
  /** Pasted data: the developer path and the built-in example (no one's payslip). */
  pasted: boolean;
  /** The payslips checked, with the findings shown; `source` only when read from a file. */
  payslips: readonly { source?: { file: string; page: number }; findings: readonly Finding[] }[];
  /** The names of the files a Form 106 was read from (a failed read has none). */
  form106Files: readonly string[];
  /** The tax years of the Form 106 forms typed in by hand (undefined: no year given); each counts as a document (Matt, 2026-10-23 22:56). */
  form106Typed?: readonly (number | undefined)[];
  /** The Form 106 refund estimate's low end (agorot; 0 for a year we don't estimate). */
  refundLowAgorot: number;
  /** A translation ("Translate my payslip"): it shows no findings, so no amount is sent, only the documents (Matt, 2026-10-24 12:59). */
  translation?: boolean;
}

/**
 * What the counter gets for a check (Matt, 2026-10-22): the documents read (each payslip page, each Form 106 read
 * from a file and each Form 106 typed in; typed-in payslip figures aren't documents) and the money worth checking (each worth-checking figure rounded down
 * to ₪100, added up, plus the refund estimate's low end). Pasted data and the built-in example add nothing at all.
 */
export function checkCount(c: CheckInput): CheckCount {
  if (c.pasted) return { documents: [], shekels: 0 };
  return {
    documents: [
      ...c.payslips.flatMap((p) => (p.source ? [`p:${p.source.file}#${p.source.page}`] : [])),
      ...c.form106Files.map((name) => `f:${name}`),
      // A typed-in form is a document too: one key per form (its year and its place), so it counts once per visit.
      ...(c.form106Typed ?? []).map((year, i) => `t:${year ?? ''}:${i}`),
    ],
    shekels: c.translation ? 0 : c.payslips.reduce((sum, p) => sum + p.findings.reduce((s, f) => s + roundedLow(claimableTotal([f])), 0), 0) + roundedLow(c.refundLowAgorot),
  };
}

/** The new documents and the rise of the money since the last count; nothing new → no request. Failures are ignored. */
// eslint-disable-next-line no-restricted-globals -- the usage counter, the one approved network exception (CLAUDE.md)
export function countCheck(check: CheckCount, url = COUNTER_URL, doFetch: typeof fetch = fetch): void {
  if (!url || countingOff()) return;
  const fresh = [...new Set(check.documents)].filter((k) => !countedDocs.has(k));
  for (const k of fresh) countedDocs.add(k);
  // An odd amount (not a finite number) counts as 0, so it can't block the rest of the visit's counting.
  const shekels = Number.isFinite(check.shekels) ? Math.max(0, Math.floor(check.shekels / 100) * 100) : 0;
  const rise = Math.max(0, shekels - sentShekels);
  sentShekels = Math.max(sentShekels, shekels);
  const documents = Math.min(MAX_DOCUMENTS, fresh.length);
  if (documents === 0 && rise === 0) return;
  // A plain-text body (no preflight): the two numbers and nothing else.
  void doFetch(url, { method: 'POST', keepalive: true, body: payload(documents, Math.min(rise, MAX_FOUND)), ...OPTIONS }).catch(() => undefined);
}

/** The largest amount one request may add (₪), as the Worker's MAX_FOUND. */
export const MAX_FOUND = 1_000_000;

/** The totals (documents checked; rounded figures added up, whole shekels), or undefined (no counter, offline, an odd reply). */
// eslint-disable-next-line no-restricted-globals -- the usage counter, the one approved network exception (CLAUDE.md)
export async function readTotal(url = COUNTER_URL, doFetch: typeof fetch = fetch): Promise<{ documents: number; estimatedFound: number } | undefined> {
  if (!url || countingOff()) return undefined;
  try {
    const res = await doFetch(url, { method: 'GET', ...OPTIONS });
    if (!res.ok) return undefined;
    const body = (await res.json()) as { documents?: unknown; estimatedFound?: unknown };
    const ok = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0;
    // A Worker not yet updated has no document count: no line rather than a wrong one.
    return ok(body.documents) && ok(body.estimatedFound) ? { documents: body.documents, estimatedFound: body.estimatedFound } : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The counter line's two figures are marked in the message text itself: `<n>…</n>` around the documents count with its
 * word and around the amount with its unit (Matt, 2026-10-23 20:13), so new wording keeps the styling without code
 * changes. Returns the text cut into plain pieces and marked pieces; text without markers comes back as one plain piece.
 */
export function markedParts(text: string): { text: string; marked: boolean }[] {
  const parts: { text: string; marked: boolean }[] = [];
  for (const [i, piece] of text.split(/<\/?n>/).entries()) if (piece) parts.push({ text: piece, marked: i % 2 === 1 });
  return parts;
}
